import { useMemo, useState } from "react";
import { BomItemRow } from "./BomTree";
import { inr } from "./CostSummary";

export default function BomTable({ items, onSave }) {
  const [q, setQ] = useState("");
  const [proc, setProc] = useState("");
  const [sort, setSort] = useState({ key: "item_number", dir: 1 });

  const filtered = useMemo(() => {
    let out = items;
    if (q) {
      const lq = q.toLowerCase();
      out = out.filter((i) => i.description.toLowerCase().includes(lq)
        || (i.material_spec || "").toLowerCase().includes(lq) || i.item_number.includes(lq));
    }
    if (proc) out = out.filter((i) => i.procurement_type === proc);
    return [...out].sort((a, b) => {
      const va = a[sort.key], vb = b[sort.key];
      if (sort.key === "item_number")
        return sort.dir * (a.item_number.split(".").map(Number)[0] * 100 + (a.item_number.split(".").map(Number)[1] || 0)
          - (b.item_number.split(".").map(Number)[0] * 100 + (b.item_number.split(".").map(Number)[1] || 0)));
      return sort.dir * ((va ?? -Infinity) > (vb ?? -Infinity) ? 1 : -1);
    });
  }, [items, q, proc, sort]);

  const Th = ({ k, children, right }) => (
    <th className={`py-2 px-2 cursor-pointer hover:text-[var(--c-text)] ${right ? "text-right" : ""}`}
      onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }))}
      data-testid={`sort-${k}`}>
      {children} {sort.key === k ? (sort.dir > 0 ? "▲" : "▼") : ""}
    </th>
  );

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-hidden" data-testid="bom-table">
      <div className="flex items-center gap-2 p-2 border-b border-[var(--c-border)]">
        <input data-testid="bom-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search description, spec, item #…"
          className="w-72 h-7 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2 outline-none focus:border-[var(--c-accent)]" />
        <select data-testid="bom-proc-filter" value={proc} onChange={(e) => setProc(e.target.value)}
          className="h-7 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2">
          <option value="">All procurement</option>
          <option value="in_house">In-house</option>
          <option value="bought_out">Bought-out</option>
        </select>
        <span className="ml-auto font-mono text-[10px] text-[var(--c-muted)]">{filtered.length} / {items.length} items · {inr(filtered.reduce((s, i) => s + (i.total_cost || 0), 0))}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--c-border)] text-left font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">
              <Th k="item_number">Item</Th><th className="py-2 px-2">Description</th>
              <Th k="quantity" right>Qty</Th><th className="py-2 px-2">Unit</th>
              <Th k="total_weight" right>Wt (kg)</Th><Th k="unit_cost" right>Unit Cost</Th>
              <Th k="total_cost" right>Total</Th><Th k="vendor">Vendor</Th><Th k="procurement_type">Proc.</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => <BomItemRow key={it.item_number} item={it} onSave={onSave} />)}
            {!filtered.length && (
              <tr><td colSpan={9} className="py-8 text-center text-[11px] text-[var(--c-muted)]">No items match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
